import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { Chart } from 'chart.js'; // Chart 객체 import
import 'chart.js/auto';
import { ClipboardList } from 'lucide-react';
import { API_BASE_URL } from '../../config/config';
import tokens from '../../design/tokens.json';

/**
 * 측정기록 탭 — 기록 추세 차트와 건강 수치 상세 이력.
 *
 * DashboardDetail 이 2,200줄이라 개요를 그 위에 더 쌓지 않고 떼어 냈다.
 * 데이터는 여전히 DashboardDetail 이 한 번 받아서 props 로 내려준다 —
 * 탭을 옮길 때마다 같은 API 를 다시 부르지 않기 위해서다.
 */

/* 차트 색은 토큰에서만 온다(design.md 1절). tokens.color.chart 를 그냥 chart 로 풀지
 * 않는 것은 이 파일의 Chart.js 콜백들이 인자 이름으로 chart 를 쓰기 때문이다 — 가려진다.
 *
 * 눈금 '글자'에 chart.axis 를 쓰지 않는다. 흰 배경에서 대비가 3.04:1 이라 본문 기준
 * (4.5:1)에 못 미친다. 축 선 같은 비텍스트에는 되지만 글자는 text-2(7.11:1)를 쓴다. */
const chartColor = tokens.color.chart;
const statusColor = tokens.color.status;
const textColor = tokens.color.text;

// drawSafeRanges 함수 추가 (ControlCenter.js/DashboardDetail.js와 동일하게)
const drawSafeRanges = (chart) => {
    const { ctx, chartArea, scales } = chart;
    if (!ctx || !chartArea || !scales) return;
    const safeRanges = chart.options.safeRanges;
    if (!Array.isArray(safeRanges)) return;
    safeRanges.forEach(range => {
        if (!range || range.min == null || range.max == null) return;
        ctx.save();
        ctx.fillStyle = range.color;
        ctx.fillRect(
            chartArea.left,
            scales.y.getPixelForValue(range.max),
            chartArea.right - chartArea.left,
            scales.y.getPixelForValue(range.min) - scales.y.getPixelForValue(range.max)
        );
        ctx.beginPath();
        ctx.strokeStyle = range.lineColor;
        ctx.lineWidth = 2;
        ctx.moveTo(chartArea.left, scales.y.getPixelForValue(range.max));
        ctx.lineTo(chartArea.right, scales.y.getPixelForValue(range.max));
        ctx.moveTo(chartArea.left, scales.y.getPixelForValue(range.min));
        ctx.lineTo(chartArea.right, scales.y.getPixelForValue(range.min));
        ctx.stroke();
        ctx.restore();
    });
};

// 플러그인 등록 (한 번만)
Chart.register({
    id: 'safeRangesPlugin',
    beforeDraw: (chart) => {
        drawSafeRanges(chart);
    }
});

export default function RecordsTab({ activeMetric, onMetricChange, chartData, allHealthData }) {
    const [systolicRange, setSystolicRange] = useState([]);
    const [diastolicRange, setDiastolicRange] = useState([]);
    const [showHealthDataModal, setShowHealthDataModal] = useState(false);

    useEffect(() => {
        if (activeMetric === 'BP_SYSTOLIC' || activeMetric === 'BP_DIASTOLIC') {
            fetchAllSafeRanges();
        }
    }, [activeMetric]);

    // 안전범위 값 세팅 (컴포넌트 내부에서 activeMetric 참조)
    const getSafeRanges = () => {
        if (activeMetric === 'BP_SYSTOLIC') {
            return [
                { min: 90, max: 120, color: chartColor['band-normal'], lineColor: statusColor.normal.line },
                { min: 120, max: 140, color: chartColor['band-caution'], lineColor: statusColor.caution.line }
            ];
        } else if (activeMetric === 'BP_DIASTOLIC') {
            return [
                { min: 60, max: 80, color: chartColor['band-normal'], lineColor: statusColor.normal.line },
                { min: 80, max: 90, color: chartColor['band-caution'], lineColor: statusColor.caution.line }
            ];
        } else if (activeMetric === 'BLOOD_GLUCOSE') {
            return [
                { min: 70, max: 100, color: chartColor['band-normal'], lineColor: statusColor.normal.line },
                { min: 100, max: 125, color: chartColor['band-caution'], lineColor: statusColor.caution.line }
            ];
        } else if (activeMetric === 'BLOOD_GLUCOSE_MEAL') {
            return [
                { min: 90, max: 140, color: chartColor['band-normal'], lineColor: statusColor.normal.line },
                { min: 140, max: 200, color: chartColor['band-caution'], lineColor: statusColor.caution.line }
            ];
        } else if (activeMetric === 'BMI') {
            return [
                { min: 18.5, max: 23, color: chartColor['band-normal'], lineColor: statusColor.normal.line },
                { min: 23, max: 25, color: chartColor['band-caution'], lineColor: statusColor.caution.line }
            ];
        }
        return [];
    };

    // fetchSafeRange에서 BP_SYSTOLIC, BP_DIASTOLIC일 때만 NORMAL, WARNING 구간 모두 배열로 반환, 나머지는 기존대로 NORMAL만 반환
    const fetchSafeRange = async (measureType) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/measurement-signal-config`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { measure_type: measureType }
            });
            console.log('measurement-signal-config response', measureType, response.data.data);
            if (response.data && response.data.success && response.data.data) {
                if (measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC') {
                    // NORMAL, WARNING 구간 모두 추출
                    const ranges = response.data.data
                        .filter(row => row.signal_type === 'NORMAL' || row.signal_type === 'WARNING')
                        .map(row => {
                            let min = Number(row.greater_than_equal);
                            let max = row.lower_than_equal !== null && row.lower_than_equal !== undefined
                                ? Number(row.lower_than_equal)
                                : Number(row.lower_than);
                            // 하한값 보정
                            if (measureType === 'BP_SYSTOLIC' && min < 90) min = 90;
                            if (measureType === 'BP_DIASTOLIC' && min < 60) min = 60;
                            return {
                                min,
                                max,
                                color: row.signal_type === 'NORMAL'
                                    ? chartColor['band-normal']
                                    : chartColor['band-caution'],
                                lineColor: row.signal_type === 'NORMAL'
                                    ? statusColor.normal.line
                                    : statusColor.caution.line
                            };
                        });
                    return ranges;
                } else {
                    // 기존 로직 유지 (NORMAL만)
                    const normalConfig = response.data.data.find(row => row.signal_type === 'NORMAL');
                    if (normalConfig) {
                        const min = Number(normalConfig.greater_than_equal);
                        const max = normalConfig.lower_than_equal !== null && normalConfig.lower_than_equal !== undefined
                            ? Number(normalConfig.lower_than_equal)
                            : Number(normalConfig.lower_than);
                        return {
                            min,
                            max,
                            text: measureType === 'BLOOD_GLUCOSE' ? `정상 혈당 범위 (${min}~${max})` : `정상 BMI 범위 (${min}~${max})`
                        };
                    }
                }
            }
            return measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC' ? [] : null;
        } catch (e) {
            return measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC' ? [] : null;
        }
    };

    // fetchAllSafeRanges에서 두 타입 모두 병렬로 받아 각각 배열로 저장
    const fetchAllSafeRanges = async () => {
        console.log('fetchAllSafeRanges called');
        const [sysRanges, diaRanges] = await Promise.all([
            fetchSafeRange('BP_SYSTOLIC'),
            fetchSafeRange('BP_DIASTOLIC')
        ]);
        console.log('sysRanges', sysRanges, 'diaRanges', diaRanges);
        setSystolicRange(sysRanges);
        setDiastolicRange(diaRanges);
        return { sysRanges, diaRanges };
    };

    // 기록 추세 차트 데이터 구성
    // 차트 데이터와 옵션 설정
    const getLineChartData = () => {
        if (!chartData || chartData.length === 0) {
            return {
                labels: [],
                datasets: [{
                    label: '데이터 없음',
                    data: [],
                    borderColor: chartColor['6'],
                    backgroundColor: 'transparent',
                    fill: false
                }]
            };
        }

        const processedData = chartData.reduce((acc, item) => {
            const date = item.measure_date;
            if (!acc[date]) {
                acc[date] = {};
            }
            if (item.measure_type === 'BP_SYSTOLIC') {
                acc[date].systolic = item.current_value;
            } else if (item.measure_type === 'BP_DIASTOLIC') {
                acc[date].diastolic = item.current_value;
            } else if (item.measure_type === 'BLOOD_GLUCOSE') {
                acc[date].glucose = item.current_value;
            } else if (item.measure_type === 'BLOOD_GLUCOSE_MEAL') {
                acc[date].glucose_meal = item.current_value;
            } else if (item.measure_type === 'BMI') {
                acc[date].bmi = item.current_value;
            }
            return acc;
        }, {});

        const dates = Object.keys(processedData);

        switch (activeMetric) {
            case 'BLOOD_GLUCOSE':
                return {
                    labels: dates,
                    datasets: [{
                        label: '혈당',
                        data: dates.map(date => ({
                            x: date,
                            y: processedData[date].glucose,
                            value: processedData[date].glucose
                        })),
                        borderColor: chartColor['1'],
                        backgroundColor: 'transparent',
                        fill: false,
                        tension: 0.4
                    }]
                };
            case 'BLOOD_GLUCOSE_MEAL':
                return {
                    labels: dates,
                    datasets: [{
                        label: '식후혈당',
                        data: dates.map(date => ({
                            x: date,
                            y: processedData[date].glucose_meal,
                            value: processedData[date].glucose_meal
                        })),
                        borderColor: chartColor['3'],
                        backgroundColor: 'transparent',
                        fill: false,
                        tension: 0.4
                    }]
                };
            case 'BP_SYSTOLIC':
                return {
                    labels: dates,
                    datasets: [
                        {
                            label: '수축기',
                            data: dates.map(date => ({
                                x: date,
                                y: processedData[date].systolic,
                                value: processedData[date].systolic
                            })),
                            borderColor: chartColor['1'],
                            backgroundColor: 'transparent',
                            fill: false,
                            tension: 0.4
                        },
                        {
                            label: '이완기',
                            data: dates.map(date => ({
                                x: date,
                                y: processedData[date].diastolic,
                                value: processedData[date].diastolic
                            })),
                            borderColor: chartColor['2'],
                            backgroundColor: 'transparent',
                            fill: false,
                            tension: 0.4
                        }
                    ]
                };
            case 'BMI':
                return {
                    labels: dates,
                    datasets: [{
                        label: '체질량지수',
                        data: dates.map(date => ({
                            x: date,
                            y: processedData[date].bmi,
                            value: processedData[date].bmi
                        })),
                        borderColor: chartColor['5'],
                        backgroundColor: 'transparent',
                        fill: false,
                        tension: 0.4
                    }]
                };
            default:
                return null;
        }
    };

    const lineChartData = getLineChartData();

    const lineChartOptions = {
        scales: {
            y: {
                beginAtZero: false,
                suggestedMin: activeMetric === 'BLOOD_GLUCOSE' ? 40 :
                    activeMetric === 'BMI' ? 15 :
                        40, // BP_SYSTOLIC의 경우
                suggestedMax: activeMetric === 'BLOOD_GLUCOSE' ? 200 :
                    activeMetric === 'BMI' ? 35 :
                        180, // BP_SYSTOLIC의 경우
                title: {
                    display: true,
                    text: activeMetric === 'BLOOD_GLUCOSE' ? '혈당 수치' :
                        activeMetric === 'BMI' ? 'BMI 수치' :
                            '혈압 수치'
                },
                ticks: { color: textColor['2'] },
                grid: { color: chartColor.grid },
            },
            x: {
                display: true,
                title: {
                    display: true,
                },
                reverse: false,
                offset: true,
                ticks: {
                    maxTicksLimit: 14,
                    callback: function (value, index, values) {
                        const label = this.getLabelForValue(index);
                        // label이 날짜+시간이든 날짜만이든 MM-DD만 추출
                        if (typeof label === 'string') {
                            const datePart = label.split(' ')[0];
                            const dateObj = new Date(datePart);
                            if (!isNaN(dateObj)) {
                                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                                const day = String(dateObj.getDate()).padStart(2, '0');
                                return `${month}-${day}`;
                            }
                        }
                        return label;
                    },
                    rotation: 90,
                    minRotation: 90,
                    maxRotation: 90,
                    align: 'center',
                    color: textColor['2']
                },
                grid: {
                    display: true,
                    drawOnChartArea: true,
                    drawTicks: true,
                    offset: true,
                    color: chartColor.grid
                }
            }
        },
        plugins: {
            legend: {
                display: true, // 범례 표시
                position: 'top',
                labels: {
                    color: textColor['2'],
                },
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.raw.value !== null) {
                            label += context.raw.value;
                        }
                        return label;
                    }
                }
            }
        },
        responsive: true,
        maintainAspectRatio: false,
        elements: {
            point: {
                radius: 0
            }
        }
    };

    const processedAllData = allHealthData.reduce((acc, item) => {
        const date = item.measure_date ? item.measure_date.split('T')[0] : '';
        if (!date) return acc;
        if (!acc[date]) acc[date] = {};
        if (item.measure_type === 'BP_SYSTOLIC') acc[date].systolic = item.current_value;
        else if (item.measure_type === 'BP_DIASTOLIC') acc[date].diastolic = item.current_value;
        else if (item.measure_type === 'BLOOD_GLUCOSE') acc[date].glucose = item.current_value;
        else if (item.measure_type === 'BLOOD_GLUCOSE_MEAL') acc[date].glucose_meal = item.current_value;
        else if (item.measure_type === 'BMI') acc[date].bmi = item.current_value;
        return acc;
    }, {});
    const sortedDates = Object.keys(processedAllData).sort((a, b) => new Date(b) - new Date(a));

    return (
        <>
            <div className="records-tab__card">
                <div className="records-tab__card-head">
                    <h2 className="records-tab__card-title">기록 추세</h2>
                    <button
                        className="sds-btn sds-btn--secondary sds-btn--sm"
                        onClick={() => setShowHealthDataModal(true)}
                    >
                        <ClipboardList size={16} aria-hidden="true" />수치 상세 보기
                    </button>
                </div>
                <div className="sds-seg sds-seg--lg records-tab__metrics">
                    {[
                        ['BLOOD_GLUCOSE', '식전/공복혈당'],
                        ['BLOOD_GLUCOSE_MEAL', '식후혈당'],
                        ['BP_SYSTOLIC', '혈압'],
                        ['BMI', '체질량지수'],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => onMetricChange(key)}
                            className={`sds-seg__item${activeMetric === key ? ' is-active' : ''}`}
                            aria-current={activeMetric === key ? 'true' : undefined}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <div className="records-tab__chart">
                    {chartData && chartData.length > 0 ? (
                        <Line
                            data={lineChartData}
                            options={{
                                ...lineChartOptions,
                                safeRanges: activeMetric === 'BP_SYSTOLIC' ? [...(Array.isArray(systolicRange) ? systolicRange : []), ...(Array.isArray(diastolicRange) ? diastolicRange : [])] : getSafeRanges()
                            }}
                        />
                    ) : (
                        <div className="records-tab__empty">
                            기록 데이터가 없습니다.
                        </div>
                    )}
                </div>
            </div>

            {showHealthDataModal && (
                <div className="modal-overlay">
                    <div className="modal-content umd-memo-table-modal" style={{ maxWidth: '800px', width: '90%' }}>
                        <div className="modal-header" style={{ marginBottom: '16px' }}>
                            <h3>건강 수치 상세 이력</h3>
                            <button onClick={() => setShowHealthDataModal(false)} className="umd-memo-table-close-btn" style={{ marginLeft: 'auto' }}>닫기</button>
                        </div>
                        <div className="sds-table-wrap records-tab__history">
                            <table className="sds-table">
                                <thead>
                                    <tr>
                                        <th>측정일시</th>
                                        <th className="sds-table__center">혈압 (mmHg)</th>
                                        <th className="sds-table__center">식전혈당 (mg/dL)</th>
                                        <th className="sds-table__center">식후혈당 (mg/dL)</th>
                                        <th className="sds-table__center">체질량지수 (BMI)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedDates.length === 0 ? (
                                        <tr><td colSpan="5" className="sds-table__center sds-table__muted">기록이 없습니다.</td></tr>
                                    ) : (
                                        sortedDates.map(date => {
                                            const data = processedAllData[date];
                                            const bpSystolic = data.systolic !== undefined ? Math.round(data.systolic) : '-';
                                            const bpDiastolic = data.diastolic !== undefined ? Math.round(data.diastolic) : '-';
                                            const bpStr = (bpSystolic !== '-' && bpDiastolic !== '-') ? `${bpSystolic} / ${bpDiastolic}` : '-';
                                            const glucose = data.glucose !== undefined ? Math.round(data.glucose) : '-';
                                            const glucoseMeal = data.glucose_meal !== undefined ? Math.round(data.glucose_meal) : '-';
                                            const bmi = data.bmi !== undefined ? Number(data.bmi).toFixed(1) : '-';

                                            return (
                                                <tr key={date}>
                                                    <td>{date}</td>
                                                    <td className="sds-table__center">{bpStr}</td>
                                                    <td className="sds-table__center">{glucose}</td>
                                                    <td className="sds-table__center">{glucoseMeal}</td>
                                                    <td className="sds-table__center">{bmi}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
